import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../../services/apiService";
import DataTable, {
  createActionsColumn,
  DataTableButton,
  DataTableFilters,
  DataTableSelect,
} from "../../../components/data-table";
import AlertNotification from "../../../components/notification";
import DeleteModal from "../../../components/models/deleteModal.jsx";
import HrEmployeeModal from "./HrEmployeeModal";
import UserAvatar from "../../../components/ui/UserAvatar";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const STATUS_BADGE = {
  active:     "bg-green-50 border-green-200 text-green-700",
  probation:  "bg-yellow-50 border-yellow-200 text-yellow-700",
  resigned:   "bg-gray-100 border-gray-200 text-gray-500",
  terminated: "bg-red-50 border-red-200 text-red-700",
};

function HrEmployees() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id: editingId } = useParams();
  // The modal is driven by the URL: /hr/employees/new and /hr/employees/:id/edit.
  const showModal = pathname.endsWith("/new") || Boolean(editingId);
  const [employees, setEmployees]   = useState([]);
  const [locations, setLocations]   = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [notification, setNotification] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting]     = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState("");

  const [search, setSearch]     = useState("");
  const [filterLoc, setFilterLoc]   = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (search)       params.search     = search;
      if (filterLoc)    params.locationId = filterLoc;
      if (filterStatus) params.status     = filterStatus;
      const res = await api.get("/hr/employees", { params, headers: authHeaders() });
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch {
      setNotification({ text: "Failed to load employees.", status: false });
    } finally {
      setIsLoading(false);
    }
  }, [search, filterLoc, filterStatus]);

  useEffect(() => {
    api.get("/hr/locations", { params: { activeOnly: true }, headers: authHeaders() })
       .then((r) => setLocations(Array.isArray(r.data) ? r.data : []));
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const openCreate = () => { setSubmitError(""); navigate("/hr/employees/new"); };
  const openEdit   = (emp) => { setSubmitError(""); navigate(`/hr/employees/${emp.id}/edit`); };
  const closeModal = () => { if (isSubmitting) return; setSubmitError(""); navigate("/hr/employees"); };

  const handleSubmit = async (payload) => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      if (editingId) {
        await api.put(`/hr/employees/${editingId}`, payload, { headers: authHeaders() });
        setNotification({ text: "Employee updated.", status: true });
      } else {
        await api.post("/hr/employees", payload, { headers: authHeaders() });
        setNotification({ text: "Employee added.", status: true });
      }
      navigate("/hr/employees");
      fetchEmployees();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || "Failed to save employee.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await api.delete(`/hr/employees/${deleteTarget.id}`, { headers: authHeaders() });
      setNotification({ text: "Employee deleted.", status: true });
      setDeleteTarget(null);
      fetchEmployees();
    } catch {
      setNotification({ text: "Failed to delete employee.", status: false });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    { key: "employeeCode", label: "Code", sortable: true,
      render: (v) => <span className="font-mono text-xs text-app-secondary">{v || "—"}</span> },
    { key: "fullName", label: "Name", sortable: true,
      render: (v, row) => (
        <span className="flex items-center gap-2">
          <UserAvatar name={v} src={row.credentials?.avatarThumbUrl} seed={row.credentials?.id || row.id} size="sm" />
          <span className="min-w-0">
            <span className="block font-medium text-app-secondary">{v}</span>
            {row.designation && <span className="block text-xs text-app-muted">{row.designation}</span>}
          </span>
        </span>
      ) },
    { key: "credentials.email", label: "Linked Account", sortable: true,
      render: (v) => (v ? <span className="text-app-secondary">{v}</span> : <span className="text-app-muted">Not linked</span>) },
    { key: "email", label: "Email", sortable: true, render: (v) => v || "—" },
    { key: "location.name", label: "Location", sortable: true, render: (v) => v || "—" },
    { key: "employmentType", label: "Type", sortable: true,
      render: (v) => <span className="capitalize">{v?.replace("_", " ") || "—"}</span> },
    { key: "workMode", label: "Mode", sortable: true, render: (v) => <span className="capitalize">{v || "—"}</span> },
    { key: "status", label: "Status", sortable: true,
      render: (v) => (
        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[v] || ""}`}>
          {v}
        </span>
      ) },
    createActionsColumn((emp) => [
      { label: "View details", icon: Eye, onSelect: () => navigate(`/hr/employees/${emp.id}`) },
      { label: "Edit", icon: Pencil, onSelect: () => openEdit(emp) },
      { label: "Delete", icon: Trash2, tone: "danger", onSelect: () => setDeleteTarget(emp) },
    ], { getRowLabel: (emp) => emp.fullName }),
  ];

  return (
    <div className="space-y-5">
      <p className="text-sm text-app-muted">Manage HR employee records.</p>

      <DataTable
        ariaLabel="Employees"
        columns={columns}
        data={employees}
        loading={isLoading}
        loadingRowCount={8}
        searchable
        manualFiltering
        searchPlaceholder="Search name or email…"
        onSearchChange={setSearch}
        filterable
        activeFilterCount={[filterLoc, filterStatus].filter(Boolean).length}
        filterContent={
          <DataTableFilters
            canClear={Boolean(filterLoc || filterStatus)}
            onClear={() => { setFilterLoc(""); setFilterStatus(""); }}
          >
            <DataTableSelect
              id="hr-employees-location"
              label="Location"
              value={filterLoc}
              onChange={setFilterLoc}
              options={[{ value: "", label: "All Locations" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
            />
            <DataTableSelect
              id="hr-employees-status"
              label="Status"
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: "", label: "All Status" },
                ...["active", "probation", "resigned", "terminated"].map((s) => ({
                  value: s,
                  label: s.charAt(0).toUpperCase() + s.slice(1),
                })),
              ]}
            />
          </DataTableFilters>
        }
        toolbarRight={
          <DataTableButton variant="primary" icon={Plus} onClick={openCreate}>
            Add Employee
          </DataTableButton>
        }
        onRowClick={(emp) => navigate(`/hr/employees/${emp.id}`)}
        pagination
        defaultPageSize={10}
        labels={{ empty: "No employees found." }}
      />

      <HrEmployeeModal
        key={editingId ?? "new"}
        show={showModal}
        onClose={closeModal}
        onSubmit={handleSubmit}
        employeeId={editingId}
        locations={locations}
        isSubmitting={isSubmitting}
        error={submitError}
      />
      {notification && <AlertNotification text={notification.text} status={notification.status} onClose={() => setNotification(null)} />}
      <DeleteModal
        show={Boolean(deleteTarget)}
        handleClose={() => { if (!isDeleting) setDeleteTarget(null); }}
        handleConfirm={handleDelete}
        message={`Delete employee "${deleteTarget?.fullName}"?`}
        loading={isDeleting}
      />
    </div>
  );
}

export default HrEmployees;
